'use client';

import { useStore } from 'zustand';
import { faArrowRight, faDesktop } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { addressFormatter, formatGoogleMapsAddress } from '@/utils/formatters/format-address';
import { Icon } from '@/components/ui/icon.component';
import { isPreciseLocation } from '@/utils/formatters/location/is-precise-location.util';
import { formatDistance } from '@/utils/formatters/format-distance';
import { userLocationStore } from '@/store/user-location.store';
import { Link } from '@/components/ui/link/link.component';
import { googleMapsBaseUrl } from '@/utils/consts/google-maps.consts';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface SearchPartnerProfileAddressProps {
  partner: SearchPartnerData['partner'];
  telehealth: boolean;
  distance: number;
}

export const SearchPartnerProfileAddress = ({ partner, telehealth, distance }: SearchPartnerProfileAddressProps) => {
  const hasPreciseLocation = useStore(userLocationStore, (state) => isPreciseLocation(state?.userLocation));
  const addressToSearch = formatGoogleMapsAddress(partner.facility);
  const googleMapsLink = `${googleMapsBaseUrl}${addressToSearch}`;
  const analytics = getAnalyticsPlugin();

  const trackOpenMapClick = () => {
    analytics.track(AnalyticsEnum.CLICK_OPEN_MAP_SEARCH_RESULT, { partner_id: partner.id });
  };

  return (
    <div className="hidden xl:block xl:mt-2">
      {telehealth
        ? (
            <div className="flex items-center" data-testid={DataTestIdEnum.SEARCH_CARD_PARTNER_PROFILE_ADDRESS_TELEHEALTH_INDICATOR}>
              <Icon icon={faDesktop} className="text-gray-700 mr-2 h-5 w-5" size="lg" />
              <Typography variant={TypographyVariant.BODY_TEXT} className="text-gray-700">
                Teleconsulta
              </Typography>
            </div>
          )
        : (
            <>
              <div className="flex">
                {hasPreciseLocation && (
                  <Typography
                    variant={TypographyVariant.BODY_TEXT}
                    className="text-gray-700 font-semibold"
                  >
                    {formatDistance(distance)}
                     &nbsp;km&nbsp;•&nbsp;
                  </Typography>
                )}
                <Typography
                  variant={TypographyVariant.BODY_TEXT}
                  className="text-gray-700"
                  data-testid={DataTestIdEnum.SEARCH_CARD_PARTNER_PROFILE_ADDRESS_ADDRESS_LINK}
                >
                  {addressFormatter(partner.facility, 'fullWithoutZipcode')}
                </Typography>
              </div>
              <Link
                prefetch={false}
                href={googleMapsLink}
                className="inline-flex items-center gap-2 py-2 font-semibold text-green-500 hover:underline"
                target="_blank"
                data-testid={DataTestIdEnum.SEARCH_CARD_PARTNER_PROFILE_ADDRESS_MAP_LINK}
                onClick={trackOpenMapClick}
              >
                Abrir no mapa
                <Icon icon={faArrowRight} className="text-green-500" />
              </Link>
            </>
          )}
    </div>
  );
};
